import { Link } from "react-router-dom";
import { PackageSearch } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/ui/core";

export interface ProductNotFoundProps {
  /** El slug que se pidió. Es lo único que identifica lo que no se encontró. */
  slug: string;
}

/**
 * El producto de esta dirección no existe.
 *
 * ── Por qué no parece un error del sistema ───────────────────────────
 * Nada de rojo ni de icono de alerta: un 404 aquí no significa que algo
 * se haya roto. Significa que el enlace es viejo, que el producto se
 * despublicó, o que hay una letra de más en la dirección. Pintarlo como
 * una falla hace que alguien reporte un problema que no existe — la misma
 * distinción que ya hace `ShopEmpty` frente a `ShopError`.
 *
 * ── Siempre hay una salida ───────────────────────────────────────────
 * El catálogo. Un callejón sin salida en una tienda es una venta perdida:
 * quien llegó buscando algo concreto probablemente compre otra cosa si se
 * le enseña dónde mirar.
 *
 * ── El slug se enseña ────────────────────────────────────────────────
 * En monoespaciada, para que se lea como un identificador y no como el
 * nombre del producto. Es lo que hay que pegar en un mensaje de soporte
 * cuando alguien dice "tu enlace no funciona".
 */
export function ProductNotFound({ slug }: ProductNotFoundProps) {
  const { t } = useTranslation();

  return (
    <div className="rounded-ds-xl border border-ds-border bg-white/70 px-6 py-16 text-center">
      <span className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-ds-lg border border-ds-lavender-bg-2 bg-ds-mist/40">
        <PackageSearch className="h-7 w-7 text-ds-lavender" aria-hidden="true" />
      </span>

      <h1 className="text-[20px] font-semibold text-ds-ink">
        {t("No encontramos este producto")}
      </h1>

      <p className="mx-auto mt-3 max-w-sm text-[13px] leading-relaxed text-ds-ink-3">
        {t("Puede que ya no esté disponible o que la dirección tenga un error.")}
      </p>

      {slug && (
        <p className="mx-auto mt-3 max-w-full truncate font-mono text-[12px] text-ds-ink-3">
          /producto/{slug}
        </p>
      )}

      <Button as={Link} to="/shop" variant="dark" size="sm" className="mt-6">
        {t("Ver el catálogo")}
      </Button>
    </div>
  );
}
